import type {
  BOTH_INCLUSION_TYPE,
  DATA_LAKE_INCLUSION_TYPE,
  INDEXER_INCLUSION_TYPE,
  INTERNAL_DATA_LAKE_TYPE,
  EXTERNAL_DATA_LAKE_TYPE,
} from 'data-lake/Constants';
import type { ActivePreviewSearch, DataLakeURLQueryParams, DataLakeStorageType } from 'data-lake/preview/Types';
import type Search from 'views/logic/search/Search';
import type { QueryString } from 'views/logic/queries/types';

export type DataLakeType = typeof INTERNAL_DATA_LAKE_TYPE | typeof EXTERNAL_DATA_LAKE_TYPE;

export enum DATA_LAKE_CONFIG_ACTION {
  Create = 'create',
  Update = 'update',
  Delete = 'delete',
}

export type RetrievalResult = {
  id: string;
  restore_indices: Array<RestoreIndex>;
  timestamp_from: string;
  timestamp_to: string;
  retrieved_at: string;
  index_failures: number;
  partially_retrieved: boolean;
  retrieved_documents: number;
};

export type RestoreIndex = {
  index_set_id: string;
  index_name: string;
};

export type DataLake = {
  id: string;
  archive_config_id: string;
  message_count: number;
  archive_name: string;
  timestamp_from: string;
  timestamp_to: string;
  stream_id: string;
  last_known_stream_title: string;
  restore_history: Array<RetrievalResult>;
};

export type DataLakeConfig = {
  archive_name: string;
  stream_ids: Array<string>;
  retention_time: string;
  id: string;
  enabled: boolean;
};

export enum JobStatus {
  Cancelled = 'cancelled',
  Complete = 'complete',
  Error = 'error',
  Paused = 'paused',
  Runnable = 'runnable',
  Running = 'running',
}

export enum JobType {
  Restore = 'restore',
  Optimize = 'optimize',
  Delete = 'delete',
}

export type DataLakeJob = {
  id: string;
  job_type: JobType;
  action: string;
  archive: Array<string>;
  timestamp_from: string;
  timestamp_to: string;
  is_cancelable: boolean;
  is_ackable: boolean;
  target_index_name: string;
  status: JobStatus;
  percent_done: number;
  started_at: string;
  next_time: string;
  stream_ids: Array<string>
};

export type DataLakeActiveBackend = {
  active_backend: string;
};

export type StreamDataLakeConfig = {
  id: string;
  archive_name: string;
  enabled: boolean;
  stream_id: string;
  retention_time: string;
};

export type StreamDataLakeStatus = {
  enabled: boolean;
};

export type FieldsOperator = 'AND' | 'OR';

export type FieldFilters = {
  field_name: string;
  value: string | Array<string>;
};

export type InclusionType =
  | typeof DATA_LAKE_INCLUSION_TYPE
  | typeof INDEXER_INCLUSION_TYPE
  | typeof BOTH_INCLUSION_TYPE;

export type EstimateParams = {
  to: string;
  from: string;
  dataLakeId: string;
  streamIds: Array<string>;
  inclusionType?: InclusionType;
  fieldFilters?: Array<FieldFilters>;
  operator?: FieldsOperator;
};

export type EstimateType = {
  from: string;
  to: string;
  size_estimates: {
    [_key: string]: {
      table_identifier: string;
      archive_name: string;
      row_count: number;
      size_bytes: number;
      files_count: number;
    };
  };
  row_count: number;
  total_size_bytes: number;
  total_files_count: number;
};

export type DataLakeActionFormValues = {
  nextTimeRange: {
    type: 'absolute';
    from: string;
    to: string;
  };
  fullDelete?: boolean;
  wipeRestores?: boolean;
  inclusionType?: InclusionType;
  dataLakeId?: string;
  streamIds?: Array<string>;
  fieldFilters?: Array<FieldFilters>;
  operator?: FieldsOperator;
};
export type DataLakeActionValues = {
  from: string;
  to: string;
  full_delete?: boolean;
  wipe_restores?: boolean;
  inclusion_type?: InclusionType;
  stream_ids?: Array<string>;
  field_filters?: Array<FieldFilters>;
  operator?: FieldsOperator;
  data_lake_id?: string;
};

type ExtraValidation = (formValues: unknown) => string | undefined;

export type RetrievalFormFieldsComponentProps = {
  submitButtonText: string;
  hourOnly: boolean;
};

export type DataLakePlugin = {
  icon: React.ComponentType;
  searchType: string;
  queryType: QueryString['type'];
  storageType: DataLakeStorageType;
  previewComponent: React.ComponentType<{
    initialSearch: Search | undefined;
    initialSearchJob:
      | { status: ActivePreviewSearch['status']; id: string; nodeId: string; progress: number }
      | undefined;
    initialUrlParams: DataLakeURLQueryParams;
    resetUrlQueryParams: () => void;
  }>;
  extraValidations?: {
    [field: string]: ExtraValidation;
  };
  retrievalFormFieldsComponent?: React.ComponentType<RetrievalFormFieldsComponentProps>;
};

declare module 'graylog-web-plugin/plugin' {
  interface PluginExports {
    dataLakeStorageTypes?: Array<DataLakePlugin>;
  }
}

export type DataLakeField = {
  field_name: string;
  class: string;
  filtering: 'DB' | 'IN_MEMORY' | 'NONE';
};
